import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';

import VismaInviceTableRow from './VismaInviceTableRow';
import InvoiceDialogContent from './dialogContent/InvoiceDialogContent';
import TableDialog from '../TableDialog';
import {Button, Layout, ListItem, Select, SelectValue, Table, Text, Tooltip} from '@phonectas/unifon-ui-kit';
import {LIMIT_PAYLOAD} from 'common/constants';
import FilterIcon from 'common/components/icons/FilterIcon';
import {StyledIconButton} from 'common/components/styled';
import {Invoice, VismaRestkontoDownloadRequest} from 'common/types/store/threeSixtyTypes';
import {StyledSectionDiv} from 'common/commonStyled';
import {StyledTableFilterRow} from '../styled';
import VismaResAccDialog from './VismaResAccDialog';

interface VismaInvoiceTableProps {
	id: string;
}

interface StyledIconButtonProps extends React.ComponentProps<typeof StyledIconButton> {
  $width?: number;
  $height?: number;
}

const VismaInvoiceTable: React.FC<VismaInvoiceTableProps> = ({id}) => {
	const [t] = useTranslation();
	const {
		getVismaList,
		getVismaReskonto,
	} = useThreeSixtyNewStore((state) => ({
		getVismaList: state.getVismaList,
		getVismaReskonto: state.getVismaReskonto,
	}), shallow);
	const [activeRow, setActiveRow] = useState<Invoice | null>(null);
	const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
	const [invoiceDialog, setInvoiceDialog] = useState(false);
	const [selectedBillingGroup, setSelectedBillingGroup] = useState<string[]>([]);
	const statusFilters = useMemo(() => (['OPEN', 'CLOSED', 'ALL'] as const), []);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('OPEN');
	const [showRestAccDialog, setShowRestAccDialog] = useState(false);
	// const [batchChecked, setBatchChecked] = useState<string[]>([]);
	type StatusFilter = typeof statusFilters[number];
	const {
		totalPages,
		// accountNotFound,
		// visma,
		vismaData,
		pagesShown,
		accountName,
		billingGroups,
	} = useThreeSixtyNewStore((state) => ({
		totalPages: state?.accountTables?.visma?.total_pages,
		accountNotFound: state.accountNotFound,
		accountName: state?.activeAccount?.account_details?.name,
		visma: state?.accountTables?.visma,
		vismaData: state?.accountTables?.visma?.invoices,
		pagesShown: state?.accountTables?.visma?.page,
		billingGroups: state?.accountTables?.visma?.all_billing_groups,
	}), shallow);

	// const handleCheckboxChange = useCallback(
	// 	(value: string) => {
	// 		setBatchChecked(prev => {
	// 			if (prev.includes(value)) {
	// 				return prev.filter(num => num !== value);
	// 			} else {
	// 				return [...prev, value];
	// 			}
	// 		});
	// 	},
	// 	[]
	// );

	const onShowRestAccDialog = useCallback(() => {
		setShowRestAccDialog(true);
	}, []);
	const onHideRestAccDialog = useCallback(() => {
		setShowRestAccDialog(false);
	}, []);
	// Remove duplicates from billing groups
	const uniqueBillingGroups = useMemo(() => {
		if (!billingGroups) return [];
		return [...new Set(billingGroups)];
	}, [billingGroups]);

	const uniqueClientIds = useMemo(() => {
		if (!vismaData) return [];
		const clientIds = vismaData.map(invoice => invoice.client_no);
		return [...new Set(clientIds)];
	}, [vismaData]);

	// Filter visma data based on selected billing group
	const filteredVismaData = useMemo(() => {
		if (!vismaData || selectedBillingGroup.length === 0) return vismaData || [];
		return vismaData.filter(invoice => selectedBillingGroup.includes(invoice.customer_no));
	}, [vismaData, selectedBillingGroup]);

	const statusColors: Record<StatusFilter, string> = {
		OPEN: '#47AC45',
		CLOSED: '#FF0000',
		ALL: '#0000FF',
	};

	const onStatusFilterChange = useCallback(() => {
		setStatusFilter((prevStatus) => {
			const currentIndex = statusFilters.indexOf(prevStatus);
			const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % statusFilters.length;
			return statusFilters[nextIndex] ?? 'OPEN';
		});
	}, [statusFilters]);
	
	const onShowInvoiceDialog = useCallback(() => {
		setInvoiceDialog(true);
	}, []);

	const onHideInvoiceDialog = useCallback(() => {
		setInvoiceDialog(false);
	}, []);

	const onRowClick = useCallback((item: Invoice, index: number) => {
		setActiveRow(item);
		setActiveRowIndex(index);
		onShowInvoiceDialog();
	}, [onShowInvoiceDialog]);

	// const handleBatchSelect = useCallback(() => {
	// 	console.log('Batch select clicked', batchChecked);
	// }, [batchChecked]);

	const onBillingGroupChange = useCallback((val: SelectValue) => {
		const selectedValues = Array.isArray(val) ? val : [val];
		const selectedStrings = selectedValues.map(v => String(v));

		if (selectedStrings.includes('selectall')) {
			setSelectedBillingGroup(uniqueBillingGroups);
			return;
		}

		if (selectedStrings.includes('deselectall')) {
			setSelectedBillingGroup([]);
			return;
		}

		const filtered = selectedStrings.filter(item => item !== 'All' && item !== 'selectall' && item !== 'deselectall');

		setSelectedBillingGroup(filtered);
	}, [uniqueBillingGroups]);

	const handleDownloadReskonto = useCallback(({data}: {data: VismaRestkontoDownloadRequest}) => {
		if (!data) return;
		if (!data.account_id) return;
		getVismaReskonto({data, onSuccess: () => {
			onHideRestAccDialog();
		}});
	}, [getVismaReskonto, onHideRestAccDialog]);

	const onDefaultResKonto = useCallback(() => {
		const idToInt = Number(id);
		getVismaReskonto({
			data: {
				account_id: idToInt,
				format: 'excel',
				use_entire_account: true,
			},
		});
	}, [id, getVismaReskonto]);

	const onLoadMore = useCallback((size: number) => {
		if (totalPages === pagesShown) return;

		getVismaList({
			id: id,
			page: 1,
			pageSize: size + LIMIT_PAYLOAD,
			status: statusFilter,
			billingGroups: selectedBillingGroup,

		});
	}, [id, totalPages, getVismaList, pagesShown, statusFilter, selectedBillingGroup]);

	const renderItems = useCallback((item: Invoice, index: number) => (
		<VismaInviceTableRow
			key={index}
			onClick={() => onRowClick(item, index)}
			data={item}
			index={index}
			activeRow={activeRowIndex}
			// batchChecked={batchChecked}
			// setBatchChecked={handleCheckboxChange}
		/>
	), [
		onRowClick,
		activeRowIndex,
		// batchChecked,
		// handleCheckboxChange,
	]);

	useEffect(() => {
		getVismaList({
			id: String(id),
			page: 1,
			pageSize: 50,
			status: statusFilter,
			billingGroups: selectedBillingGroup,
		});
	}, [id, statusFilter, selectedBillingGroup, getVismaList]);

	return (
		<StyledSectionDiv
			style={{
				display: 'flex',
				flexDirection: 'column',
				flexGrow: '1',
				overflow: 'hidden',
				padding: '16px 8px'
			}}
			$endDiv
			$fullHeight
		>
			<StyledTableFilterRow gap="none" justifyContent="space-between">
				<Layout nowrap alignItems="center" gap="xs"  fullWidth>
					<Layout nowrap alignItems="center" gap="none" style={{width: '129px'}}>
						<Select
							compact
							fullWidth
							value={selectedBillingGroup}
							onChange={onBillingGroupChange}
							multiple
						>
							{[
								<ListItem
									key="select-deselect-all"
									value={selectedBillingGroup.length === 0 ? 'selectall' : 'deselectall'}
									primaryText={
										<Text variant="body1" color="primary">
											{selectedBillingGroup.length === 0
												? t('Select all')
												: t('Deselect all')}
										</Text>
									}
								/>,
								...uniqueBillingGroups.map((group) => (
									<ListItem
										key={group}
										value={group}
										primaryText={group}
										selected={selectedBillingGroup.includes(group)}
									/>
								))
							]}
						</Select>
					</Layout>
					<Layout></Layout>
					<Layout></Layout>
				</Layout>
				<Layout direction="row" nowrap style={{minWidth: '150px'}} justifyContent="end">
					{/* { batchChecked.length > 0 && (
						<Button
							compact
							variant="alt"
							onClick={handleBatchSelect}
						>
							{t('Batch selected')} ({batchChecked.length})
						</Button>
					)} */}
					<Button
						onClick={onDefaultResKonto}
						compact
						variant="alt"
					>
						{t('Ledger account')}
					</Button>
					<Button
						onClick={onShowRestAccDialog}
						compact
						variant="alt"
					>
						{t('Ava')}
					</Button>
					<VismaResAccDialog
						show={showRestAccDialog}
						title={t('Ledger account')}
						onClose={onHideRestAccDialog}
						noPadding
						noPaddingHeader
						width={950}
						accountName={accountName || ''}
						id={id}
						onDownloadReskonto={handleDownloadReskonto}
						billingGroups={uniqueBillingGroups}
						clientIds={uniqueClientIds}
					/>
				</Layout>
			</StyledTableFilterRow>
			<div
				style={{
					width: '100%',
					overflow: 'auto',
					height: '100%',
					borderRadius: '8px',
				}}
			>
				<Table
					items={filteredVismaData}
					renderItem={(item, index) => renderItems(item, index)}
					virtualized
					onScrollDown={onLoadMore}
				>
					<Table.Row style={{padding: '0px'}}>
						{/* <Table.Cell width={20}></Table.Cell> */}
						<Table.Cell width={130}>{t('Billing grp')}</Table.Cell>
						<Table.Cell>{t('Invoice nr')}</Table.Cell>
						<Table.Cell width={80}>
							<Layout nowrap alignItems="center" gap="xs">
								<Text color="secondary" variant='caption' style={{fontSize: '12px'}}>
									{t('Status')}
								</Text>
								<Tooltip text={statusFilter}>
									<StyledIconButton
										{...({$width: 24, $height: 22} as StyledIconButtonProps)}
										style={{marginLeft: '5px', maxHeight: '22px'}}
										onClick={onStatusFilterChange}
									>
										<FilterIcon color={statusColors[statusFilter]}/>
									</StyledIconButton>
								</Tooltip>
							</Layout>
						</Table.Cell>
						<Table.Cell>{t('Due date')}</Table.Cell>
						<Table.Cell>{t('Remaining amount')}</Table.Cell>
						<Table.Cell>{t('Debt collection case')}</Table.Cell>
						<Table.Cell>{t('Overdue notice')}</Table.Cell>
						<Table.Cell>{t('Type')}</Table.Cell>
						<Table.Cell>{t('Client account')}</Table.Cell>
					</Table.Row>
				</Table>
			</div>
			<TableDialog
				show={invoiceDialog}
				width={950}
				onClose={onHideInvoiceDialog}
				noOverflow
			>
				{activeRow && (
					<InvoiceDialogContent
						data={activeRow}
						id={id}
						onClose={onHideInvoiceDialog}
					/>
				)}
			</TableDialog>
		</StyledSectionDiv>
	);
}

export default React.memo(VismaInvoiceTable);
